import {
  APP_PREVIEW_CONTENT_HOSTS,
  APP_PREVIEW_PLATFORM,
  APP_PREVIEW_TENANT_HEADER,
} from '@/lib/constants/appPreview';
import type { AppV1Browse, AppV1Cfg } from '@/lib/types/AppV1';

export class AppPreviewApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AppPreviewApiError';
  }
}

/**
 * Direct browser client for the public /app/v1 contract (D-2.6). Deliberately
 * NOT built on WebApi: these are unauthenticated cross-origin reads of public
 * content data — the dashboard session token must never leave our API host.
 */
export class AppPreviewApi {
  /**
   * Brand-matched content host so white-label accounts never touch the
   * Streamhoster host. Account-based (`useResellerBrand`), not login-host-based.
   */
  static contentHost(isReseller: boolean): string {
    return isReseller ? APP_PREVIEW_CONTENT_HOSTS.whitelabel : APP_PREVIEW_CONTENT_HOSTS.streamhoster;
  }

  private static async get<T>(path: string, appDomain: string, isReseller: boolean): Promise<T> {
    const res = await fetch(`${AppPreviewApi.contentHost(isReseller)}${path}?p=${APP_PREVIEW_PLATFORM}`, {
      headers: { [APP_PREVIEW_TENANT_HEADER]: appDomain },
      // Revalidate instead of trusting the browser copy (cfg is served with a
      // strong ETag): "Refresh" must actually reflect a just-approved brand kit.
      cache: 'no-cache',
    });
    if (!res.ok) {
      throw new AppPreviewApiError(res.status, `Preview request failed (${res.status})`);
    }
    return res.json() as Promise<T>;
  }

  static getCfg(appDomain: string, isReseller: boolean): Promise<AppV1Cfg> {
    return AppPreviewApi.get<AppV1Cfg>('/app/v1/cfg', appDomain, isReseller);
  }

  static getBrowse(appDomain: string, isReseller: boolean): Promise<AppV1Browse> {
    return AppPreviewApi.get<AppV1Browse>('/app/v1/browse', appDomain, isReseller);
  }
}
